import { Modal } from 'antd';
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CookiebotDOM, CookiebotEvents } from 'src/shared/components/cookiebot-provider';

export interface AnalyticsProps {
  active?: boolean;
}

// El contenedor de GTM no esta aqui: lo carga el arranque del `<head>`, que corre antes de que React
// exista y no puede importar una constante de TypeScript. Si cambia, hay TRES copias que actualizar:
// `index.html`, `Views/Shared/_Layout.cshtml` y la shell de j2l-website.
const GA4_MEASUREMENT_ID = 'G-YMP95L0FLW';
const META_PIXEL_ID = '1710350250324765';
const HUBSPOT_PORTAL_ID = '144070489';
const CONTENTSQUARE_SRC = 'https://t.contentsquare.net/uxa/a56c4d3171602.js';

/**
 * `resolved` es la distincion que no se puede perder: separa «Cookiebot todavia no ha contestado» de
 * «ha contestado y lo ha denegado todo». Sin ella las dos situaciones son el mismo par de booleanos a
 * `false`, y entonces el primer render emite un `consent update` denegandolo todo por encima del
 * `granted` que Cookiebot ya habia mandado.
 *
 * `preferences` no decide que se inyecta, porque ninguna de estas herramientas guarda preferencias
 * del visitante. Esta porque Consent Mode si tiene dos senales que dependen de ella y que el bloque
 * de arranque deja denegadas, asi que alguien tiene que levantarlas.
 */
interface ConsentState {
  resolved: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
}

const UNRESOLVED: ConsentState = { resolved: false, preferences: false, statistics: false, marketing: false };

/**
 * Puede no existir todavia: el script de Cookiebot lo inyecta la shell HTML y llega de forma
 * asincrona, asi que entre el montaje de React y su carga `window.Cookiebot` es `undefined`.
 */
const getCookiebot = (): CookiebotDOM | undefined => (typeof window === 'undefined' ? undefined : (window.Cookiebot as CookiebotDOM | undefined));

const readConsent = (): ConsentState => {
  const cookiebot = getCookiebot();
  if (cookiebot?.hasResponse !== true) return UNRESOLVED;

  return {
    resolved: true,
    preferences: cookiebot.consent?.preferences === true,
    statistics: cookiebot.consent?.statistics === true,
    marketing: cookiebot.consent?.marketing === true
  };
};

const sameConsent = (a: ConsentState, b: ConsentState): boolean =>
  a.resolved === b.resolved && a.preferences === b.preferences && a.statistics === b.statistics && a.marketing === b.marketing;

/**
 * Sigue el consentimiento vivo, no solo el del primer render.
 *
 * `CookiebotOnConsentReady` basta por si solo: lo dispara tanto la carga con consentimiento ya
 * almacenado en la cookie como el momento en que el visitante responde al banner o lo renueva.
 * `CookiebotOnAccept` y `CookiebotOnDecline` estan como redundancia, para reaccionar al primero que
 * llegue: aqui el consentimiento decide ademas que etiquetas se inyectan y si hay que recargar, asi
 * que llegar tarde cuesta mas que suscribirse de mas.
 *
 * El arranque del `<head>` y `Views/Shared/_Layout.cshtml` escuchan solo `CookiebotOnConsentReady`, y
 * es correcto por lo mismo: alli no hay nada que inyectar.
 *
 * No sirve el `useCookiebot` del provider: guarda el propio objeto `window.Cookiebot`, que es
 * siempre la misma referencia, asi que React descarta el re-render y el cambio de consentimiento no
 * se ve. Aqui el estado son booleanos, que si cambian de valor.
 */
const useCookiebotConsent = (active: boolean): ConsentState => {
  const [consent, setConsent] = useState<ConsentState>(UNRESOLVED);

  const sync = useCallback(() => {
    const next = readConsent();
    setConsent((prev) => (sameConsent(prev, next) ? prev : next));
  }, []);

  useEffect(() => {
    if (!active) return;

    // Por si Cookiebot ya habia resuelto el consentimiento antes de que este efecto se suscribiera.
    // Aqui es la norma y no la excepcion: `index.tsx` no renderiza hasta que resuelve
    // `waitForHealthyBackendServices()`, que es una ida y vuelta a la red.
    sync();

    const events = [CookiebotEvents.CookiebotOnConsentReady, CookiebotEvents.CookiebotOnAccept, CookiebotEvents.CookiebotOnDecline];
    events.forEach((e) => window.addEventListener(e, sync));
    return () => events.forEach((e) => window.removeEventListener(e, sync));
  }, [active, sync]);

  return consent;
};

/**
 * Inyecta una sola vez: el `id` hace de guarda, asi que un re-render no duplica la etiqueta.
 *
 * `data-cookieconsent="ignore"` no es una laguna. Estas etiquetas ya estan detras del consentimiento
 * por el efecto que las inyecta, y sin la marca pasarian ademas por el clasificador del bloqueo
 * automatico, que es una segunda puerta con criterio propio: puede bloquear algo que nosotros si
 * hemos concedido, y puede reejecutar un script inline al conceder el consentimiento, que en el
 * fragmento de Meta significa un `PageView` duplicado. Una sola puerta, y es esta.
 */
const injectScript = (id: string, options: { src?: string; text?: string; parent?: HTMLElement }): void => {
  if (document.getElementById(id) != null) return;

  const script = document.createElement('script');
  script.id = id;
  script.setAttribute('data-cookieconsent', 'ignore');
  if (options.text != null) script.text = options.text;
  // `src` al final: con el bloqueo automatico activo, Cookiebot observa la asignacion de `src` para
  // decidir, y la exencion tiene que estar puesta ya cuando eso ocurre.
  if (options.src != null) {
    script.async = true;
    script.src = options.src;
  }

  (options.parent ?? document.head).appendChild(script);
};

/**
 * Carga las etiquetas de analitica y de marketing, cada una detras de su consentimiento.
 *
 * Lo que NO esta aqui, y es deliberado:
 *
 * - **GTM, el bloque de Consent Mode y el `update` que lo sigue** viven en `index.html`, que va sin
 *   comentarios a proposito porque Vite lo sirve tal cual al navegador. El porque es este:
 *
 *   Tienen que ejecutarse durante el parseo del `<head>`, antes que cualquier otra etiqueta; desde
 *   React es imposible, porque para cuando React monta el `<head>` ya se parseo. Ese era el fallo de
 *   Jobs-2-Live/j2l-identity#122.
 *
 *   **El orden es Consent Mode, Cookiebot, GTM**, y no es el orden en que lo escribe el articulo de
 *   Cookiebot (https://support.cookiebot.com/hc/en-us/articles/360009192739). Alli GTM aparece antes,
 *   pero su fragmento lleva `async = true` y la etiqueta de Cookiebot no lleva nada, o sea que bloquea
 *   el parser: el orden de EJECUCION que produce su ejemplo es el de aqui. Y el que importa es el de
 *   ejecucion, porque el bloqueo automatico solo alcanza a lo que se inserta despues de que `uc.js`
 *   haya corrido.
 *
 *   **`async = false` tiene que estar en los dos.** Un script creado con `createElement` nace con la
 *   marca de "force async"; quitarsela lo mete en la lista que ejecuta EN ORDEN DE INSERCION, y los
 *   que la conservan van a otra coleccion sin orden. Entre las dos no hay relacion: dejar GTM
 *   asincrono no lo pone detras de Cookiebot, lo pone a competir, y googletagmanager suele ganar esa
 *   carrera. El precio de encolarlo es que su EJECUCION espera; las descargas siguen en paralelo y
 *   `wait_for_update: 500` ya presupuesta esa espera. Si `uc.js` falla, la especificacion lo saca de
 *   la lista y GTM continua: no es una dependencia dura.
 *
 *   **Dos guardas del arranque que no se pueden quitar.** `if (!window.REACT_APP_COOKIEBOT_KEY) return;`
 *   es la que hace que sin clave no se cargue NADA: quitarla deja GTM entrando sin ningun CMP detras.
 *   Y el `data-cookieconsent="ignore"` de GTM no es un bypass del consentimiento aunque lo parezca: el
 *   contenedor tiene que cargar para poder recibir la senal, y lo que decide que dispara dentro es
 *   Consent Mode. En el `load` de alli, `src` se asigna el ultimo por lo mismo que en `injectScript`
 *   de aqui: Cookiebot observa esa asignacion para decidir si bloquea, asi que los atributos tienen
 *   que estar puestos antes.
 *
 *   **El `update` se emite tambien desde alli**, suscrito a `CookiebotOnConsentReady` antes de cargar
 *   Cookiebot. Cookiebot lo manda por su cuenta solo si su integracion con Consent Mode esta activada
 *   en el panel, que es un ajuste que este repositorio no puede comprobar y cuyo fallo seria mudo. El
 *   de alli llega dentro de la ventana de `wait_for_update`; el de aqui abajo no puede, y por eso es
 *   el respaldo para los cambios posteriores. La guarda de `hasResponse` es la misma que `readConsent`
 *   de este fichero: si se anade una senal, va en los dos sitios.
 *
 * - **Los `<noscript>` de respaldo** de GTM y de Meta. Sin JavaScript Cookiebot no puede ejecutarse,
 *   asi que no hay banner, no hay consentimiento posible y esos pixeles disparaban igual.
 *
 * Esto cubre el SPA: login, registro, recuperar y restablecer contrasena. Las cinco vistas Razor
 * (cerrar sesion, sesion cerrada, acceso denegado, error y redireccion) llevan su propio arranque en
 * `Views/Shared/_Layout.cshtml`, porque no pasan por React.
 */
/**
 * Las tres piezas que se cargan, cada una con su puerta.
 *
 * **La puerta de una herramienta es la conjuncion de TODAS las categorias bajo las que estan
 * declaradas sus cookies**, no la de la categoria que mejor la describa. El consentimiento lo da el
 * visitante contra las categorias que le ensena el banner, y ese banner lista las cookies por la
 * clasificacion del panel de Cookiebot: si la declaracion dice que `__hstc` es de estadistica y
 * alguien rechaza estadistica, esa cookie no puede escribirse, aunque quien la ponga sea una
 * herramienta que uno considere de marketing.
 *
 * De ahi la puerta doble de HubSpot: sus cookies estan declaradas entre estadistica (`__hstc`,
 * `hubspotutk`, `__hssc`, `__hssrc`) y marketing (`__ptq.gif`). Las declaradas como necesarias no
 * restringen nada, porque no requieren consentimiento. Aqui no hay LinkedIn; en el website si, y
 * alli su puerta es `marketing && preferences` por la misma regla.
 *
 * Si alguna vez se reclasifica una cookie en el panel, esto es lo que hay que mover con ella, y la
 * tabla de la politica de cookies del website detras. Las tres dicen lo mismo a proposito.
 */
type Tool = 'analytics' | 'meta' | 'hubspot';

const TOOLS: Tool[] = ['analytics', 'meta', 'hubspot'];

const gateFor = (consent: { statistics: boolean; marketing: boolean }): Record<Tool, boolean> => ({
  analytics: consent.statistics,
  meta: consent.marketing,
  hubspot: consent.statistics && consent.marketing
});

/**
 * Lo que se puede apagar en caliente al retirar el consentimiento, que no es todo.
 *
 * Meta acepta `revoke` y `grant`, asi que su apagado es reversible. HubSpot y Contentsquare solo
 * tienen apagado: cortan el envio, pero su script sigue cargado y su sesion abierta, y volver a
 * conceder no los reactiva. Por eso esto no sustituye a la oferta de recarga, que es la unica
 * respuesta completa; lo que hace es parar la medicion en el acto sin esperar a que el visitante
 * acepte recargar. GA4 no esta aqui porque de el se encarga el `consent update` de Consent Mode.
 *
 * Los tres comandos estan comprobados contra el codigo que sirven hoy los proveedores, no contra su
 * documentacion: `optout` sale de la lista de comandos de `t.contentsquare.net/uxa/...`, y
 * `doNotTrack` y `revokeCookieConsent` del bundle de `js-eu1.hs-analytics.net`.
 */
const stopVendorTracking = (stale: Record<Tool, boolean>): void => {
  if (stale.meta) window.fbq?.('consent', 'revoke');

  if (stale.hubspot) {
    // `revokeCookieConsent` ademas borra sus cookies.
    window._hsq = window._hsq ?? [];
    window._hsq.push(['doNotTrack'], ['revokeCookieConsent']);
  }

  if (stale.analytics) {
    window._uxa = window._uxa ?? [];
    window._uxa.push(['optout']);
  }
};

/**
 * La otra mitad de `stopVendorTracking`, y solo para Meta porque es el unico que vuelve.
 */
const resumeMetaTracking = (): void => {
  window.fbq?.('consent', 'grant');
};

const Analytics: FC<PropsWithChildren<AnalyticsProps>> = ({ active = false, children }) => {
  const { t } = useTranslation();
  const [modal, modalContextHolder] = Modal.useModal();
  const { resolved, preferences, statistics, marketing } = useCookiebotConsent(active);

  // Que se ha llegado a inyectar. Es lo que decide si hay algo que apagar, y no la transicion del
  // consentimiento: ver el bloque de documentacion del efecto que ofrece recargar.
  const injected = useRef<Record<Tool, boolean>>({ analytics: false, meta: false, hubspot: false });

  // El par de categorias obsoletas sobre el que ya se actuo. Sin esto, el efecto es un predicado de
  // estado y no de transicion, asi que cada reejecucion por identidad de `modal` o de `t` volveria a
  // abrir el dialogo despues de que el visitante lo hubiera cerrado con «Ahora no».
  const handled = useRef<string | null>(null);
  const everResolved = useRef(false);
  const openConfirm = useRef<ReturnType<typeof modal.confirm> | null>(null);

  /**
   * Respaldo del `update` de Consent Mode. El que cuenta lo manda el arranque del `<head>`, que se
   * suscribe a `CookiebotOnConsentReady` antes de cargar Cookiebot y por tanto llega dentro de la
   * ventana de `wait_for_update`. Este no puede llegar a tiempo a la primera medicion (no corre hasta
   * que el bundle ha parseado y el backend ha contestado), asi que su papel es cubrir los cambios de
   * consentimiento posteriores y ser idempotente con el otro.
   */
  useEffect(() => {
    if (!active) return;

    if (resolved) {
      everResolved.current = true;
      window.gtag?.('consent', 'update', {
        analytics_storage: statistics ? 'granted' : 'denied',
        ad_storage: marketing ? 'granted' : 'denied',
        ad_user_data: marketing ? 'granted' : 'denied',
        ad_personalization: marketing ? 'granted' : 'denied',
        // El bloque de arranque las deja denegadas y nadie mas las revisa. Sin esto, un visitante que
        // acepta preferencias las arrastra denegadas toda la sesion.
        functionality_storage: preferences ? 'granted' : 'denied',
        personalization_storage: preferences ? 'granted' : 'denied'
      });
      return;
    }

    // Volver a «sin respuesta» despues de haber respondido es lo que hace `Cookiebot.withdraw()`.
    // Hoy no lo llama nadie, pero esta a un ajuste del panel de Cookiebot de estarlo, y el fallo
    // seria mudo en la direccion mala: sin esto, Consent Mode se quedaria en el ultimo `granted`
    // durante el resto de la vida de la pagina. No se ofrece recargar aqui, porque en ese flujo
    // Cookiebot vuelve a ensenar el banner y encadenar un dialogo encima sobra.
    if (!everResolved.current) return;
    window.gtag?.('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied'
    });
  }, [active, resolved, preferences, statistics, marketing]);

  /**
   * Retirar el consentimiento tiene que apagar lo que ya esta cargado. Dos pasos, en este orden:
   *
   * 1. **Apagar lo que tiene interruptor.** Meta, HubSpot y Contentsquare publican su propia
   *    retirada y se llama aqui, en `stopVendorTracking`. Para la medicion en el acto, sin depender
   *    de que el visitante acepte nada.
   * 2. **Ofrecer la recarga** para el resto. Una etiqueta de terceros no se descarga: su script
   *    sigue en la pagina con su sesion abierta hasta que la pagina muera, y de HubSpot y
   *    Contentsquare ni siquiera vuelve el envio al reconceder. Recargar es la unica respuesta
   *    completa.
   *
   * **La decision se toma sobre lo que se llego a INYECTAR, no sobre como cambio el consentimiento.**
   * Con la transicion, retirar estadistica y conceder marketing despues cerraba el aviso de la
   * primera retirada -- el consentimiento «habia cambiado», aunque no hacia lo retirado -- y dejaba
   * GA4, Contentsquare y HubSpot corriendo sin que nadie volviera a avisar. `injected` convierte el
   * predicado en «hay algo cargado cuya categoria ya no esta concedida», que es exactamente lo que el
   * aviso dice, y no depende del camino por el que se llego.
   *
   * `resolved` si hace falta: `Cookiebot.renew()` devuelve el estado a «sin respuesta» y vuelve a
   * ensenar el banner. Sin esa guarda, esa ventana se leeria como retirada y ofreceria recargar con
   * el banner abierto.
   *
   * Se pregunta antes de recargar porque las cuatro pantallas de este SPA son formularios, y el
   * registro es un asistente de tres pasos que lo guarda todo en estado de React: recargar sin avisar
   * se lleva el rol, el correo, la contrasena y la casilla de condiciones, y parece una caida. El
   * foco va al boton de no recargar, que es el que no destruye nada. Si el visitante dice que no, lo
   * que no tiene interruptor sigue cargado hasta que recargue o se vaya.
   */
  useEffect(() => {
    if (!active || !resolved) return;

    // Una herramienta esta obsoleta cuando se cargo y su puerta ya no se cumple.
    const gate = gateFor({ statistics, marketing });
    const stale = TOOLS.reduce((acc, tool) => ({ ...acc, [tool]: injected.current[tool] && !gate[tool] }), {} as Record<Tool, boolean>);

    const key = TOOLS.map((tool) => stale[tool]).join('|');
    if (key === handled.current) return;
    handled.current = key;

    if (!TOOLS.some((tool) => stale[tool])) {
      // Todo lo cargado vuelve a tener su consentimiento, asi que un aviso abierto ya no describe
      // nada. Y lo que se apago y sabe volver, vuelve.
      openConfirm.current?.destroy();
      if (injected.current.meta) resumeMetaTracking();
      return;
    }

    stopVendorTracking(stale);

    // La instancia se guarda antes de asignarla y `afterClose` comprueba identidad: `destroy()` solo
    // arranca la animacion de cierre, y para cuando el `afterClose` del dialogo viejo corre, la ref ya
    // apunta al nuevo. Sin la comprobacion, el viejo al cerrarse dejaria la ref a null con el nuevo en
    // pantalla.
    openConfirm.current?.destroy();
    const instance = modal.confirm({
      title: t('Has cambiado tus preferencias de cookies'),
      content: t(
        'Hemos dejado de enviar lo que se podía parar sin recargar. El resto de herramientas ya cargadas siguen activas hasta que la página se recargue. Si recargas ahora perderás lo que hayas escrito sin guardar.'
      ),
      okText: t('Recargar ahora'),
      cancelText: t('Ahora no'),
      // antd 6 marca `autoFocusButton` como obsoleto en favor de `focusable`.
      focusable: { autoFocusButton: 'cancel' },
      // El resto del SPA no tiene un solo boton rectangular: sin esto, estos dos serian los unicos.
      okButtonProps: { shape: 'round' },
      cancelButtonProps: { shape: 'round' },
      // La promesa no se resuelve nunca a proposito: deja el boton en estado de carga mientras el
      // navegador tira la pagina, que si no invita a un segundo clic.
      onOk: () => {
        window.location.reload();
        return new Promise<void>(() => undefined);
      },
      afterClose: () => {
        if (openConfirm.current === instance) openConfirm.current = null;
      }
    });

    openConfirm.current = instance;
  }, [active, resolved, statistics, marketing, modal, t]);

  useEffect(() => () => openConfirm.current?.destroy(), []);

  useEffect(() => {
    if (!active || !statistics) return;

    injectScript('j2l-ga4', { src: `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}` });
    injectScript('j2l-ga4-config', {
      text: `
        window.dataLayer = window.dataLayer || [];
        window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', '${GA4_MEASUREMENT_ID}');
      `
    });

    injectScript('j2l-contentsquare', { src: CONTENTSQUARE_SRC });

    injected.current.analytics = true;
  }, [active, statistics]);

  useEffect(() => {
    if (!active || !marketing) return;

    injectScript('j2l-meta-pixel', {
      text: `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window,document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${META_PIXEL_ID}');
        fbq('track', 'PageView');
      `
    });

    injected.current.meta = true;
  }, [active, marketing]);

  useEffect(() => {
    // HubSpot escribe cuatro cookies declaradas como de estadistica (`__hstc`, `hubspotutk`,
    // `__hssc`, `__hssrc`) y el pixel `__ptq.gif`, declarado como publicitario. Por eso espera a las
    // dos categorias: con una sola, la otra mitad se escribiria sin su consentimiento.
    if (!active || !statistics || !marketing) return;

    // El id es el que documenta HubSpot: su propio cargador y sus integraciones identifican la
    // etiqueta por el.
    injectScript('hs-script-loader', { src: `//js-eu1.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`, parent: document.body });

    injected.current.hubspot = true;
  }, [active, statistics, marketing]);

  return (
    <>
      {modalContextHolder}
      {children}
    </>
  );
};

export default Analytics;
